/** CER-wide capabilities inherited by every configuration of that CER. */
export interface CerFeatures {
	aiSuggestions: 'enabled' | 'disabled' | 'demo';
	reports: boolean;
	anomalies: boolean;
}

export const DEFAULT_CER_FEATURES: CerFeatures = {
	aiSuggestions: 'enabled',
	reports: true,
	anomalies: true,
};

export function parseCerFeatures(value: unknown): CerFeatures | null {
	if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
	const row = value as Record<string, unknown>;
	if (
		typeof row.aiSuggestions !== 'string' ||
		!['enabled', 'disabled', 'demo'].includes(row.aiSuggestions) ||
		typeof row.reports !== 'boolean' ||
		typeof row.anomalies !== 'boolean' ||
		Object.keys(row).some((key) => !['aiSuggestions', 'reports', 'anomalies'].includes(key))
	)
		return null;
	return {
		aiSuggestions: row.aiSuggestions as CerFeatures['aiSuggestions'],
		reports: row.reports,
		anomalies: row.anomalies,
	};
}

/** Daily seeded shuffle: demos vary by CER/day but stay stable while reading. */
export function demoSuggestions<
	T extends { id: string; label: string; desc: string; datetime: string; response: string },
>(items: readonly T[], tenantId: string, day = new Date().toISOString().slice(0, 10)): T[] {
	let seed = 2166136261;
	for (const char of `${tenantId}:${day}`)
		seed = Math.imul(seed ^ char.charCodeAt(0), 16777619) >>> 0;
	const random = () => {
		seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
		return seed / 4294967296;
	};
	const shuffled = [...items];
	for (let i = shuffled.length - 1; i > 0; i--) {
		const j = Math.floor(random() * (i + 1));
		const a = shuffled[i];
		const b = shuffled[j];
		if (a && b) [shuffled[i], shuffled[j]] = [b, a];
	}
	return shuffled.slice(0, 4).map((rec, index) => ({
		...rec,
		id: `demo:${day}:${rec.id}`,
		label: `Demo · ${rec.label}`,
		desc: `Esempio dimostrativo, non basato sulle misure della CER. ${rec.desc}`,
		datetime: `${day}T${String(10 + index).padStart(2, '0')}:00:00`,
		response: `Suggerimento dimostrativo: dati e risultati sono simulati.\n\n${rec.response}`,
	}));
}
